import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Shell } from "@/components/Shell";
import { SEOHead } from "@/components/SEO";
import { api, fmtDate, fmtMonth, dollars } from "@/lib/api";
import { imageUrl } from "@/components/ImageUpload";
import { EventInfoDisplay } from "@/components/EventInfoFieldset";
import { useAuth } from "@/auth/AuthContext";
import { CalendarDays, MapPin, Users, Building2, ParkingSquare, Wrench, Lock } from "lucide-react";
import { toast } from "sonner";

export default function EventDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [ev, setEv] = useState(null);
    const [categories, setCategories] = useState([]);
    const [vendorProfile, setVendorProfile] = useState(null);
    // Distinguishes "still loading the vendor profile" from "loaded and the
    // user genuinely has no category". The original logic showed the loud
    // "Pick one on your profile" warning whenever vendorProfile was null,
    // which fired wrongly during the brief loading window AND whenever the
    // GET silently failed (try/catch swallowed the error). We only show the
    // warning after a confirmed successful load.
    const [vendorProfileLoaded, setVendorProfileLoaded] = useState(false);
    const [myApp, setMyApp] = useState(null);
    const [chosenCat, setChosenCat] = useState("");
    const [refundQuote, setRefundQuote] = useState(null);
    const [cancelOpen, setCancelOpen] = useState(false);

    // Pretty-URL routing: the route is /events/:id but the backend accepts
    // EITHER an ObjectId OR a slug, so legacy bookmarks/share links keep
    // working. If the visitor arrived via an id, we client-side-replace the
    // URL with the canonical slug (history.replaceState, not push — keeps
    // back button behavior natural and acts as the 301 from a UX standpoint).
    // Canonical <link> in <head> is the authoritative SEO signal for Google.
    useEffect(() => {
        if (!ev || !ev.event_slug) return;
        if (id !== ev.event_slug && typeof window !== "undefined") {
            const next = `/events/${ev.event_slug}${window.location.search || ""}`;
            window.history.replaceState({}, "", next);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ev?.event_slug]);

    // Single source of truth for the URL-ish identifier passed back to the
    // API. Always use what the user typed in their address bar (id OR slug);
    // the backend resolves either to the same event.
    const evKey = ev?.event_slug || id;

    const loadAll = async () => {
        const [evRes, catRes] = await Promise.all([
            api.get(`/events/${id}`),
            api.get("/categories"),
        ]);
        setEv(evRes.data);
        setCategories(catRes.data);
        if (user?.role === "vendor") {
            // Two independent loads. Previously we used `Promise.all` so if
            // EITHER call failed (e.g. the my-application endpoint 400ing on
            // a slug param before its slug-aware fix shipped), the entire
            // try block bailed and `vendorProfileLoaded` never flipped — the
            // apply UI got stuck on "Loading your profile…" forever. With
            // `Promise.allSettled` each promise's outcome is handled in
            // isolation and the apply UI can recover even if my-application
            // is temporarily broken.
            const [vpRes, maRes] = await Promise.allSettled([
                api.get("/vendor/profile"),
                api.get(`/events/${id}/my-application`),
            ]);
            if (vpRes.status === "fulfilled") {
                setVendorProfile(vpRes.value.data);
            } else {
                console.warn("EventDetail: /vendor/profile failed", vpRes.reason);
            }
            // ALWAYS flip the loaded flag — even on failure, we want the UI
            // to leave the indeterminate "Loading…" skeleton and render the
            // correct downstream state (apply form OR "no category set"
            // warning if the profile load truly returned nothing).
            setVendorProfileLoaded(true);
            if (maRes.status === "fulfilled") {
                setMyApp(maRes.value.data.application);
                if (maRes.value.data.waitlist_entry) {
                    setWaitlistJoined(true);
                    setWaitlistedCat(maRes.value.data.waitlist_entry.category_id || null);
                } else {
                    setWaitlistJoined(false);
                }
            } else {
                console.warn("EventDetail: /my-application failed", maRes.reason);
            }
        }
    };

    useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id, user?.id]);

    // Source of truth for the vendor's category is their profile primary.
    // We display it as text — no dropdown — when they have one set.
    const myPrimaryCat = useMemo(() => {
        if (!vendorProfile) return "";
        return vendorProfile.primary_category_id || (vendorProfile.categories || [])[0] || "";
    }, [vendorProfile]);

    const myCategoryId = useMemo(() => {
        // Always prefer the vendor's primary category, since a vendor may only
        // apply under that one category.
        if (myPrimaryCat) return myPrimaryCat;
        if (!vendorProfile || !ev) return "";
        return (ev.desired_categories || [])[0] || "";
    }, [myPrimaryCat, vendorProfile, ev]);

    // Pricing for the chosen category (only shown inside the apply card)
    const myCategoryPrice = useMemo(() => {
        if (!chosenCat || !ev) return null;
        const snap = (ev.pricing_snapshot || []).find((s) => s.category_id === chosenCat);
        return snap ? snap.final_price_cents : null;
    }, [chosenCat, ev]);

    useEffect(() => {
        if (myCategoryId && !chosenCat) setChosenCat(myCategoryId);
    }, [myCategoryId, chosenCat]);

    const [waitlistedCat, setWaitlistedCat] = useState(null); // category_id we hit a "full" on
    const [waitlistJoined, setWaitlistJoined] = useState(false);
    const [boothElements, setBoothElements] = useState([]);
    const [note, setNote] = useState("");
    const [noteDirty, setNoteDirty] = useState(false);

    // Load admin-managed booth element catalog once. Used to translate the
    // vendor's profile IDs into human names for the prepopulated note.
    useEffect(() => {
        api.get("/booth-elements").then((r) => setBoothElements(r.data)).catch((err) => {
            console.warn("EventDetail: booth-elements load failed", err);
        });
    }, []);

    // Seed the note textarea from the vendor's freebies + raffle prize +
    // interactive booth elements the moment we know who they are. Only
    // auto-fill until the user has manually edited the note (noteDirty guard).
    useEffect(() => {
        if (!vendorProfile || noteDirty || myApp) return;
        // interactive_booth_elements is now a free-text string; legacy data
        // may still be a list of ids that the backend has migrated to names.
        const _raw = vendorProfile.interactive_booth_elements;
        const ibeList = Array.isArray(_raw)
            ? _raw.map((id) => boothElements.find((el) => el.id === id)?.name).filter(Boolean)
            : (typeof _raw === "string" && _raw.trim()
                ? _raw.split(",").map((s) => s.trim()).filter(Boolean)
                : []);
        const lines = [];
        if (vendorProfile.freebies) lines.push(`Booth Freebies: ${vendorProfile.freebies}`);
        if (vendorProfile.raffle_prize) lines.push(`Raffle prize: ${vendorProfile.raffle_prize}`);
        if (ibeList.length) lines.push(`Interactive booth elements: ${ibeList.join(", ")}`);
        lines.push("");
        lines.push("Your pitch — what unique experience will my booth bring to your employees?");
        setNote(lines.join("\n"));
    }, [vendorProfile, boothElements, noteDirty, myApp]);

    // Pre-emptive cap detection — flips the apply card to "Category full"
    // the moment we know the vendor's primary category is at capacity for
    // this event, BEFORE they click Express interest.
    useEffect(() => {
        if (!ev || !chosenCat) { setWaitlistedCat(null); return; }
        const cap = (ev.category_capacity || []).find((c) => c.category_id === chosenCat);
        if (cap?.is_full) setWaitlistedCat(chosenCat);
        else setWaitlistedCat((prev) => (prev === chosenCat ? prev : null));
    }, [ev, chosenCat]);

    // Refund quote for the currently logged-in vendor (only when they have a
    // paid booth on this event). Used to show the exact $ they'd get back if
    // they cancel today.
    useEffect(() => {
        if (!myApp || !["paid", "confirmed"].includes(myApp.status)) {
            setRefundQuote(null);
            return;
        }
        api.get(`/refund-policy/preview?event_id=${id}`).then((r) => {
            setRefundQuote(r.data.applies_to_me);
        }).catch((err) => console.warn("refund-policy preview failed", err));
    }, [myApp, id]);

    const apply = async () => {
        if (!user) return nav(`/login?next=/events/${evKey}`);
        if (user.role !== "vendor") return toast.error("Only vendors can express interest.");
        if (!myPrimaryCat) return toast.error("Set your category on your profile first.");
        try {
            await api.post("/vendor/applications", { event_id: id, notes: note });
            toast.success("Interest submitted! The host will review your application.");
            setWaitlistedCat(null);
            await loadAll();
        } catch (err) {
            const d = err?.response?.data?.detail;
            if (err?.response?.status === 409 && d?.error === "category_full") {
                setWaitlistedCat(myPrimaryCat);
                return;
            }
            toast.error(typeof d === "string" ? d : "Could not apply");
        }
    };

    const joinWaitlist = async () => {
        try {
            await api.post("/vendor/waitlist", { event_id: id, notes: note });
            setWaitlistJoined(true);
            toast.success("You're on the waitlist.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not join the waitlist");
        }
    };

    if (!ev) return <Shell><div className="p-10 text-brand-mute">Loading…</div></Shell>;

    const catName = (cid) => categories.find((c) => c.id === cid)?.name || cid;

    const isHost = user?.role === "host_company";
    const isAdmin = user?.role === "admin";
    const isThisHost = user && ev.host_id && String(ev.host_id) === String(user.id);
    // Confirmed = vendor with paid (or attended) status for this event
    const isConfirmedVendor = myApp && ["paid", "confirmed", "attended"].includes(myApp.status);
    const isInterestedOnly = myApp && !isConfirmedVendor;
    const isAnon = ev.view_level === "anon" || !user;  // server-side strip is the source of truth
    const hero = ev.images && ev.images[0];

    // Build the public stat grid. Anonymous viewers see redacted versions of
    // the address / date / time fields. Authenticated users see full details.
    const dateLabel = ev.event_date
        ? `${fmtDate(ev.event_date)}${ev.start_time ? ` • ${ev.start_time} – ${ev.end_time || ""}` : ""}`
        : `${fmtMonth(ev.event_date_public) || "Coming soon"} — exact date after login`;
    const whereLabel = ev.location
        ? `${ev.location}${ev.city ? ", " + ev.city : ""}`
        : `${[ev.city, ev.state].filter(Boolean).join(", ") || "Location"} — exact address after login`;

    return (
        <Shell>
            {/* Canonical = slug URL. After the replaceState above, the
                window.location already reflects /events/{slug} so SEOHead
                builds the right href automatically. The og:title/description
                use the event name so social shares preview cleanly. */}
            <SEOHead
                title={ev.name}
                description={(ev.description || "").slice(0, 200) || `${ev.name} on HealthFairX.`}
                image={ev.images && ev.images[0] ? imageUrl(ev.images[0]) : undefined}
                type="event"
            />
            {/* Hero */}
            <section className="relative">
                <div className="aspect-[21/9] md:aspect-[21/7] bg-brand-bone overflow-hidden">
                    {hero ? (
                        <img src={imageUrl(hero)} alt="" className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full grain" />
                    )}
                </div>
                <div className="max-w-6xl mx-auto px-6 lg:px-10 -mt-16 relative">
                    <div className="rounded-3xl bg-white border border-brand-line p-8 md:p-10 shadow-sm">
                        <span className="chip">{ev.status.replace("_", " ")}</span>
                        <h1 className="font-heading text-3xl md:text-5xl font-extrabold mt-3 tracking-tight">{ev.name}</h1>
                        <p className="mt-3 text-brand-mute max-w-3xl leading-relaxed">{ev.description}</p>
                    </div>
                </div>
            </section>

            <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 grid lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2 space-y-8">
                    <div className="grid sm:grid-cols-2 gap-4">
                        {[
                            [CalendarDays, "When", dateLabel, !ev.event_date],
                            [MapPin, "Where", whereLabel, !ev.location],
                            [Users, "Booth capacity", `${ev.total_booth_capacity} booths`, false],
                            ...(ev.host?.company_name ? [[Building2, "Host", ev.host.company_name, false]] : []),
                        ].map(([Icon, label, val, redacted], i) => (
                            <div key={i} className={`rounded-xl border p-5 ${redacted ? "border-dashed border-brand-line bg-brand-bone/50" : "border-brand-line bg-white"}`}>
                                <div className="label-base flex items-center gap-1.5">
                                    {label}
                                    {redacted && <Lock size={11} className="text-brand-mute" />}
                                </div>
                                <div className={`font-heading font-bold mt-1 flex items-center gap-2 ${redacted ? "text-brand-mute" : ""}`}>
                                    <Icon size={16} className={redacted ? "text-brand-mute" : "text-brand-coral"} />{val}
                                </div>
                            </div>
                        ))}
                    </div>

                    {isAnon && (
                        <div className="rounded-xl border border-brand-coral/30 bg-brand-coral/5 p-5 flex flex-wrap items-center justify-between gap-3" data-testid="anon-cta-banner">
                            <div className="flex items-center gap-3">
                                <Lock size={18} className="text-brand-coral" />
                                <div>
                                    <div className="font-heading font-bold">More details after login</div>
                                    <p className="text-sm text-brand-mute mt-0.5">Sign in to see the exact address, date and time, and host contact details.</p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Link to={`/login?next=/events/${evKey}`} className="pill btn-coral px-5 py-2.5 text-sm font-semibold" data-testid="anon-login-link">Log in</Link>
                                <Link to="/register?role=vendor" className="pill px-5 py-2.5 text-sm font-semibold border border-brand-line bg-white hover:bg-brand-bone" data-testid="anon-register-link">Sign up as a vendor</Link>
                            </div>
                        </div>
                    )}

                    {/* Extra gallery from event images (skip the hero) */}
                    {ev.images && ev.images.length > 1 && (
                        <div className="grid sm:grid-cols-3 gap-3">
                            {ev.images.slice(1, 7).map((p) => (
                                <div key={p} className="rounded-xl overflow-hidden border border-brand-line aspect-[4/3] bg-brand-bone">
                                    <img src={imageUrl(p)} alt="" className="w-full h-full object-cover" />
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Host-supplied logistics + insurance metadata. Surfaced
                        BEFORE parking/setup so vendors see audience + venue
                        context up top. Gated for anonymous visitors — pods
                        still appear so they know data exists, but the exact
                        carriers/headcounts/amenities are revealed only after
                        login. Renders nothing when blank. */}
                    <EventInfoDisplay value={ev.event_info} totalEmployees={ev.total_employees} gated={!user} />

                    {/* Parking & arrival — gated to confirmed vendors, host of this event, or admin */}
                    {ev.parking_instructions && (isThisHost || isAdmin || isConfirmedVendor ? (
                        <div className="rounded-xl border border-brand-line bg-white p-6">
                            <div className="font-heading font-bold flex items-center gap-2"><ParkingSquare size={16} className="text-brand-coral" /> Parking & arrival</div>
                            <p className="text-sm text-brand-mute mt-2 whitespace-pre-line">{ev.parking_instructions}</p>
                        </div>
                    ) : isInterestedOnly ? (
                        <LockedSection title="Parking & arrival" />
                    ) : null)}

                    {/* Setup & logistics — same gating */}
                    {ev.setup_instructions && (isThisHost || isAdmin || isConfirmedVendor ? (
                        <div className="rounded-xl border border-brand-line bg-white p-6">
                            <div className="font-heading font-bold flex items-center gap-2"><Wrench size={16} className="text-brand-coral" /> Setup & logistics</div>
                            <p className="text-sm text-brand-mute mt-2 whitespace-pre-line">{ev.setup_instructions}</p>
                        </div>
                    ) : isInterestedOnly ? (
                        <LockedSection title="Setup & logistics" />
                    ) : null)}

                    <div className="rounded-xl border border-brand-line bg-white p-6">
                        <div className="font-heading font-bold">Vendor categories sought</div>
                        <div className="flex flex-wrap gap-2 mt-3">
                            {(ev.desired_categories || []).map((cid) => <span key={cid} className="chip">{catName(cid)}</span>)}
                            {(!ev.desired_categories || ev.desired_categories.length === 0) && <span className="text-sm text-brand-mute">Open to all categories.</span>}
                        </div>
                    </div>
                </div>

                <aside className="lg:col-span-1">
                    {/* Apply card – hidden for hosts and admins.
                        Available categories for the dropdown: vendor's matching cats (∩ desired), with desired as fallback. */}
                    {!isHost && !isAdmin && (
                        <div className="rounded-2xl border border-brand-line bg-white p-6 sticky top-24" data-testid="apply-panel">
                            <div className="label-base">Vendor application</div>
                            <h3 className="font-heading text-xl font-bold mt-1">
                                {myApp ? "Your application" : "Express interest"}
                            </h3>

                            {myApp ? (
                                <div className="mt-4 space-y-3">
                                    <div className="rounded-lg bg-brand-bone p-4">
                                        <div className="text-xs text-brand-mute">Status</div>
                                        <div className="font-heading font-bold capitalize">{myApp.status.replace(/_/g, " ")}</div>
                                    </div>
                                    {myApp.category_id && (
                                        <div className="text-sm text-brand-mute">Category: <span className="text-brand-ink font-semibold">{catName(myApp.category_id)}</span></div>
                                    )}
                                    {isConfirmedVendor && (
                                        <p className="text-sm text-emerald-700 bg-emerald-50 rounded-lg p-3">You're confirmed — full event logistics are now visible above.</p>
                                    )}
                                    {!isConfirmedVendor && (
                                        <p className="text-xs text-brand-mute">Parking and setup instructions become visible once your booth is confirmed.</p>
                                    )}
                                    {/* Vendor self-cancel UI. Visible for any open application status. */}
                                    {!["cancelled", "withdrawn", "declined"].includes(myApp.status) && (
                                        <div className="pt-2">
                                            {isConfirmedVendor && (
                                                <div className="rounded-lg border border-brand-line bg-white p-3 mb-3" data-testid="cancel-policy-applied">
                                                    <div className="text-xs uppercase tracking-wider text-brand-mute font-bold">Refund if you cancel today</div>
                                                    {refundQuote ? (
                                                        <div className="mt-1">
                                                            <div className="font-heading font-bold text-brand-forest">{dollars(refundQuote.amount_cents)}</div>
                                                            <div className="text-xs text-brand-mute">{refundPolicyLabel(refundQuote.policy_tier)}</div>
                                                        </div>
                                                    ) : (
                                                        <div className="text-xs text-brand-mute mt-1">Loading…</div>
                                                    )}
                                                </div>
                                            )}
                                            <button
                                                data-testid={isConfirmedVendor ? "cancel-booth-btn" : "withdraw-application-btn"}
                                                onClick={() => setCancelOpen(true)}
                                                className="pill w-full h-10 text-sm font-semibold border border-brand-coral/40 text-brand-coral bg-white hover:bg-brand-coral/5"
                                            >
                                                {isConfirmedVendor ? "Cancel my booth" : "Withdraw application"}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <p className="text-sm text-brand-mute mt-2">The host will review and let you know.</p>
                                    <div className="mt-4">
                                        <label className="label-base">Your category</label>
                                        {myPrimaryCat ? (
                                            <div className="mt-1 rounded-lg border border-brand-line bg-brand-bone px-4 py-3 flex items-center justify-between" data-testid="apply-category-locked">
                                                <span className="font-heading font-semibold">{catName(myPrimaryCat)}</span>
                                                <Link to="/vendor/profile" className="text-xs text-brand-forest font-semibold hover:underline">Change on profile</Link>
                                            </div>
                                        ) : vendorProfileLoaded ? (
                                            // Profile fetched OK, definitively no category set
                                            <div className="mt-1 rounded-lg border border-dashed border-brand-coral/40 bg-brand-coral/5 px-4 py-3 text-sm" data-testid="apply-no-category">
                                                You don't have a category set. <Link to="/vendor/profile" className="underline font-semibold text-brand-coral">Pick one on your profile</Link> before applying.
                                            </div>
                                        ) : (
                                            // Still loading the profile — show a neutral skeleton instead of
                                            // a false-positive "no category set" warning.
                                            <div className="mt-1 rounded-lg border border-brand-line bg-brand-bone/50 px-4 py-3 text-sm text-brand-mute" data-testid="apply-category-loading">
                                                Loading your profile…
                                            </div>
                                        )}
                                    </div>

                                    {chosenCat && myCategoryPrice != null && !waitlistedCat && (
                                        <>
                                            <div className="mt-4 rounded-lg bg-brand-mint p-4" data-testid="my-category-price">
                                                <div className="text-xs uppercase tracking-wider text-brand-forest font-semibold">Your booth fee</div>
                                                <div className="font-heading text-2xl font-extrabold text-brand-forest mt-0.5">{dollars(myCategoryPrice)}</div>
                                                <div className="text-xs text-brand-mute mt-1">for {catName(chosenCat)} • collected only if approved</div>
                                            </div>
                                            <div className="mt-3 rounded-lg border border-brand-line bg-white p-3 text-xs text-brand-ink" data-testid="refund-policy-prepay">
                                                <div className="font-bold uppercase tracking-wider text-[10px] text-brand-mute">Cancellation refund policy</div>
                                                <ul className="mt-1.5 space-y-0.5 text-brand-ink/80 list-disc list-inside">
                                                    <li><strong>Full refund</strong> — cancel 4+ weeks before event</li>
                                                    <li><strong>50% refund</strong> — cancel 2–4 weeks before</li>
                                                    <li><strong>No refund</strong> — cancel within 2 weeks</li>
                                                </ul>
                                                <Link to="/refund-policy" className="text-brand-forest font-semibold hover:underline mt-1.5 inline-block">Read full policy</Link>
                                            </div>
                                        </>
                                    )}

                                    {/* Vendor "sell yourself" note — pre-filled from freebies + interactive
                                        booth elements on the vendor's profile, fully editable. Sent to the
                                        host with the application or the waitlist entry. */}
                                    {myPrimaryCat && user?.role === "vendor" && (
                                        <div className="mt-4" data-testid="apply-note-block">
                                            <label className="label-base">Your pitch to the host</label>
                                            <p className="text-xs text-brand-mute mb-2">
                                                Sell yourself. What unique experience, freebie, or interactive booth element will employees walk away with? The host sees this when reviewing applicants.
                                            </p>
                                            <textarea
                                                data-testid="apply-note"
                                                rows={6}
                                                value={note}
                                                onChange={(e) => { setNote(e.target.value); setNoteDirty(true); }}
                                                className="input-base !h-auto py-3 text-sm"
                                                placeholder="e.g. We'll provide free 10-minute chair massages and a posture screening for every employee who stops by. First 20 get a $5 Starbucks card."
                                                maxLength={2000}
                                            />
                                            <div className="mt-1 text-right text-[11px] text-brand-mute">{note.length} / 2000</div>
                                        </div>
                                    )}

                                    {waitlistedCat && !waitlistJoined && (
                                        <div className="mt-4 rounded-xl border border-brand-coral/40 bg-brand-coral/5 p-4" data-testid="category-full-banner">
                                            <div className="flex items-center gap-2 text-brand-coral font-heading font-bold"><Lock size={14} /> Category full</div>
                                            <p className="text-sm text-brand-ink mt-2">This event has reached capacity for <strong>{catName(waitlistedCat)}</strong>. You can join the waitlist — if the host approves you off the list, you'll be invited to pay the booth fee{myCategoryPrice != null && <> of <strong>{dollars(myCategoryPrice)}</strong></>}.</p>
                                            <button data-testid="join-waitlist-btn" onClick={joinWaitlist} className="mt-4 pill btn-coral w-full h-11 font-semibold">Join waitlist</button>
                                            <Link to="/marketplace" className="mt-2 block text-center text-xs text-brand-forest font-semibold hover:underline">Browse alternative events →</Link>
                                        </div>
                                    )}

                                    {waitlistJoined && (
                                        <div className="mt-4 rounded-xl border border-brand-line bg-emerald-50 p-4" data-testid="waitlist-joined">
                                            <div className="font-heading font-bold text-emerald-800">You're on the waitlist</div>
                                            <p className="text-sm text-emerald-900 mt-1">We've notified the host. If a spot opens, you'll get an email with next steps.</p>
                                        </div>
                                    )}

                                    {!waitlistedCat && (
                                        <button data-testid="apply-submit" disabled={!myPrimaryCat} onClick={apply} className="pill btn-coral w-full h-11 mt-5 font-semibold disabled:opacity-50">Express interest</button>
                                    )}
                                    {!user && <p className="mt-3 text-xs text-brand-mute">You'll be asked to sign in or register first.</p>}
                                </>
                            )}
                        </div>
                    )}

                    {/* Tiny inline note for hosts/admins (no application controls). */}
                    {isThisHost && (
                        <div className="rounded-2xl border border-brand-line bg-brand-bone p-6 sticky top-24" data-testid="host-self-banner">
                            <div className="label-base">You're hosting this</div>
                            <p className="text-sm text-brand-mute mt-1">Manage applications, invite vendors, and review your roster from your host dashboard.</p>
                            <Link to={`/host/events/${ev.id}`} className="pill btn-forest inline-flex w-fit mt-4 px-5 py-2.5 text-sm font-semibold">Open in dashboard</Link>
                        </div>
                    )}
                </aside>
            </div>
            {cancelOpen && myApp && (
                <CancelBoothModal
                    application={myApp}
                    eventName={ev.name}
                    refundQuote={refundQuote}
                    onClose={() => setCancelOpen(false)}
                    onCancelled={async () => { setCancelOpen(false); await loadAll(); }}
                />
            )}
        </Shell>
    );
}

// ----------------------------------------------------------------------------- 
// Helper — translate API policy_tier into vendor-readable copy.
// ----------------------------------------------------------------------------- 
export function refundPolicyLabel(tier) {
    switch (tier) {
        case "full": return "Full refund — more than 4 weeks before the event.";
        case "partial": return "50% refund — between 2 and 4 weeks before the event.";
        case "none": return "No refund — within 2 weeks of the event.";
        case "reschedule_grace": return "Full refund — the host rescheduled this event.";
        case "event_cancel_full": return "Full refund — the host cancelled this event.";
        default: return "";
    }
}

const CancelBoothModal = ({ application, eventName, refundQuote, onClose, onCancelled }) => {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const isPaid = ["paid", "confirmed"].includes(application.status);
    const submit = async () => {
        const r = reason.trim();
        if (r.length < 3) return toast.error("Tell us why so the host knows.");
        setBusy(true);
        try {
            const res = await api.post(`/vendor/applications/${application.id}/withdraw`, { reason: r });
            const ref = res.data.refund;
            if (ref?.status === "issued") toast.success(`Cancelled. Refund of ${dollars(ref.amount_cents)} issued.`);
            else if (ref?.status === "pending") toast.success("Cancelled. A 50% refund has been queued for admin approval.");
            else if (ref?.amount_cents === 0) toast.success("Cancelled. No refund per policy (within 2 weeks of event).");
            else toast.success("Application withdrawn.");
            onCancelled();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Cancel failed");
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="cancel-booth-modal">
            <div className="bg-white rounded-2xl max-w-lg w-full p-8">
                <h2 className="font-heading text-2xl font-extrabold">
                    {isPaid ? "Cancel your booth" : "Withdraw your application"}
                </h2>
                <p className="text-sm text-brand-mute mt-1">{eventName}</p>
                {isPaid && refundQuote && (
                    <div className="mt-5 rounded-xl border border-brand-line bg-brand-bone p-4" data-testid="cancel-refund-quote">
                        <div className="text-xs uppercase tracking-wider text-brand-mute font-bold">Your refund</div>
                        <div className="font-heading text-3xl font-extrabold text-brand-forest mt-1">{dollars(refundQuote.amount_cents)}</div>
                        <div className="text-sm text-brand-mute mt-1">{refundPolicyLabel(refundQuote.policy_tier)}</div>
                        {refundQuote.policy_tier === "partial" && (
                            <p className="mt-2 text-xs text-amber-700 bg-amber-50 rounded-md p-2">
                                Partial refunds are reviewed by an admin before being issued. You'll get an email once it's processed.
                            </p>
                        )}
                    </div>
                )}
                <div className="mt-5">
                    <label className="label-base">Why are you cancelling? <span className="text-brand-coral">*</span></label>
                    <p className="text-xs text-brand-mute mb-2">The host will see this. Keep it short and specific.</p>
                    <textarea
                        data-testid="cancel-reason"
                        rows={3}
                        className="input-base !h-auto py-3"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        maxLength={1000}
                    />
                </div>
                <div className="mt-6 flex justify-end gap-3">
                    <button onClick={onClose} className="pill px-5 py-3 font-semibold border border-brand-line bg-white">Keep my booth</button>
                    <button data-testid="cancel-confirm" disabled={busy} onClick={submit} className="pill px-5 py-3 font-semibold text-white bg-brand-coral hover:bg-brand-coral/90 disabled:opacity-50">
                        {busy ? "Cancelling…" : (isPaid ? "Cancel my booth" : "Withdraw")}
                    </button>
                </div>
            </div>
        </div>
    );
};

const LockedSection = ({ title }) => (
    <div className="rounded-xl border border-dashed border-brand-line bg-white p-6">
        <div className="font-heading font-bold flex items-center gap-2 text-brand-mute"><Lock size={16} /> {title}</div>
        <p className="text-sm text-brand-mute mt-2">This information is visible to confirmed vendors only. Once your application is approved and your booth is confirmed, you'll see the details here.</p>
    </div>
);
